/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  DeepDiveTemplate,
  type DeepDiveConfig,
} from "@/components/landing/deep-dive-template";
import {
  Layers,
  Ruler,
  Crosshair,
  Box,
  LayoutList,
  Navigation,
  Upload,
} from "lucide-react";

const config: DeepDiveConfig = {
  parent: { label: "Takeoffs", href: "/features/takeoffs" },
  badge: "Plan Sets",
  title: "One plan set.",
  titleHighlight: "Not 47 loose files.",
  description:
    "Real projects include site plans, floor plans, sections, and details. Keep them together as one plan set, navigate by sheet, and manage measurements across the full document.",

  painPoint: {
    title: "Why loose files kill your takeoff accuracy",
    description:
      "When sheets are handled as separate files, teams lose continuity, duplicate effort, and miss connections between plan views.",
    bullets: [
      "Plan set stays as one document — not split into loose files on upload",
      "Page navigator for flipping between sheets (site plan, floor plan, sections, details)",
      "Per-page scale calibration handles mixed-scale plan sets correctly",
      "Cross-page volume measurements: area from the floor plan, height from the section",
      "Measurements sidebar shows ALL measurements across the entire set, not just the current page",
      "Work areas and phases persist across pages — structure doesn't reset when you change sheets",
    ],
  },

  walkthrough: {
    title: "How plan sets work in Contractor Co-Pilot",
    steps: [
      {
        title: "Upload the full set as one document",
        description:
          "Upload the complete plan set once and work through all sheets from one viewer.",
        details: [
          "One upload, one document, all pages preserved",
          "Page navigator shows all sheets with page numbers",
          "Large PDFs upload reliably for real project documentation",
          "Plan set linked to the estimate for future reference",
        ],
      },
      {
        title: "Calibrate each page to its own scale",
        description:
          "Different sheets use different scales. Calibrate each page once, then move between sheets with the right scale applied.",
        details: [
          "Per-page scale stored independently",
          "Two-click calibration on each sheet",
          "Navigate between pages and the right scale applies",
          "Recalibrate any page without affecting others",
        ],
      },
      {
        title: "Measure across pages",
        description:
          "Use the full set together: measure on one sheet, reference supporting dimensions from another, and keep all quantities in one organized sidebar.",
        details: [
          "Cross-page volume: area from one page, height from another",
          "Measurements sidebar lists EVERY measurement across the set",
          "Review measurements by page and work area",
          "Click a measurement in the sidebar to select it and keep working",
        ],
      },
    ],
  },

  details: {
    title: "Plan set specifics",
    items: [
      {
        icon: Navigation,
        title: "Page Navigator",
        description:
          "Move through sheets from one persistent page list without leaving your takeoff workflow.",
      },
      {
        icon: Crosshair,
        title: "Per-Page Scale",
        description:
          "Store calibration by page so mixed-scale plan sets stay accurate throughout takeoff.",
      },
      {
        icon: Box,
        title: "Cross-Page Volume",
        description:
          "Build volume quantities using dimensions from multiple sheets when the project requires it.",
      },
      {
        icon: LayoutList,
        title: "Full-Set Measurements Sidebar",
        description:
          "Review measurements across the entire set in one place and jump to the right sheet quickly.",
      },
      {
        icon: Layers,
        title: "Work Areas Span Pages",
        description:
          "Create a work area on the site plan and keep adding measurements to it from the floor plan and the detail sheets. The structure persists across page changes.",
      },
      {
        icon: Ruler,
        title: "One Set, One Populate",
        description:
          "Populate the estimate with measurements from every sheet, organized by work area for cleaner production.",
      },
    ],
  },

  siblings: [
    {
      label: "Blueprint Editor",
      href: "/features/takeoffs/blueprint-editor",
      icon: Upload,
      description: "Upload PDFs, navigate plan sets, zoom and pan",
    },
    {
      label: "Measurement Tool",
      href: "/features/takeoffs/measurement-tool",
      icon: Ruler,
      description:
        "Area, linear, count, volume, scale calibration, quick entry",
    },
  ],
};

export function PlanSetsPageClient() {
  return <DeepDiveTemplate config={config} />;
}
